import React from 'react';
import { ArrowRight, Brain, Target, ChatsCircle, TrendUp, CheckCircle, Sparkle, Lightning, ChartBar, Users, Upload, Gauge } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const navigate = useNavigate();

  const scrollToFeatures = () => {
    const featuresSection = document.getElementById('features-section');
    if (featuresSection) {
      featuresSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white border-b border-black/5 sticky top-0 z-10">
        <div className="px-6 md:px-8 lg:px-12 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-black tracking-tighter" data-testid="landing-title">TalentoX</h1>
          <div className="flex items-center gap-4">
            <button
              onClick={scrollToFeatures}
              className="hidden md:block text-sm font-medium text-[#52525B] hover:text-[#0033FF] transition-colors"
            >
              How It Works
            </button>
            <button
              onClick={() => navigate('/auth')}
              className="bg-[#0033FF] text-white rounded-none font-medium px-6 py-2 hover:bg-[#002AE6] transition-colors"
              data-testid="get-started-button"
            >
              Get Started Free
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#F9F9FB]">
        <div className="px-6 md:px-8 lg:px-12 max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <span className="inline-block px-4 py-1 bg-[#0033FF]/10 text-[#0033FF] text-xs font-bold uppercase tracking-wider mb-6">
              AI-Powered Career Intelligence
            </span>
          </div>
          <h2 className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter font-black leading-none mb-6 max-w-5xl mx-auto" data-testid="hero-heading">
            Transform Career Readiness with AI-Driven Insights
          </h2>
          <p className="text-lg md:text-xl font-light leading-relaxed text-[#52525B] mb-8 max-w-3xl mx-auto">
            Get instant resume analysis, job readiness scoring, and AI interview preparation. 
            Bridge skill gaps faster and hire with confidence.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <button
              onClick={() => navigate('/auth')}
              className="bg-[#0033FF] text-white rounded-none font-bold px-8 py-4 hover:bg-[#002AE6] transition-colors flex items-center justify-center gap-2"
              data-testid="cta-start-button"
            >
              Start Free Analysis
              <ArrowRight size={20} weight="bold" />
            </button>
            <button
              onClick={scrollToFeatures}
              className="bg-white text-[#0A0A0A] border-2 border-black/10 rounded-none font-bold px-8 py-4 hover:bg-black/5 transition-colors"
              data-testid="learn-more-button"
            >
              See How It Works
            </button>
          </div>

          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm font-light text-[#A1A1AA]">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-[#0033FF]" weight="fill" />
              <span>No Credit Card Required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-[#0033FF]" weight="fill" />
              <span>Instant Results</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} className="text-[#0033FF]" weight="fill" />
              <span>AI-Powered Analysis</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-y border-black/5">
        <div className="px-6 md:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-black tracking-tighter text-[#0033FF] mb-2">75%</div>
              <div className="text-sm font-medium text-[#52525B] uppercase tracking-wider">Faster Hiring</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black tracking-tighter text-[#0033FF] mb-2">90%</div>
              <div className="text-sm font-medium text-[#52525B] uppercase tracking-wider">Less Screening Time</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black tracking-tighter text-[#0033FF] mb-2">85%</div>
              <div className="text-sm font-medium text-[#52525B] uppercase tracking-wider">Match Accuracy</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-black tracking-tighter text-[#0033FF] mb-2">10x</div>
              <div className="text-sm font-medium text-[#52525B] uppercase tracking-wider">Better Quality</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - 3 Phases */}
      <section id="features-section" className="py-24 md:py-32 bg-[#F9F9FB]">
        <div className="px-6 md:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl sm:text-5xl tracking-tight font-black leading-tight mb-4">
              Three Phases. Visible Outcomes.
            </h3>
            <p className="text-lg font-light text-[#52525B] max-w-3xl mx-auto">
              Our AI-powered platform transforms career development in three simple steps.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Phase 1 */}
            <div className="bg-white border border-black/5 p-8" data-testid="phase-upload">
              <div className="w-16 h-16 bg-[#0033FF] flex items-center justify-center mb-6">
                <Upload size={32} className="text-white" weight="duotone" />
              </div>
              <div className="text-6xl font-black text-[#0033FF]/20 mb-4">1</div>
              <h4 className="text-2xl font-bold mb-4">Upload Your Resume</h4>
              <ul className="space-y-3 text-[#52525B] font-light">
                <li className="flex items-start gap-2">
                  <span className="text-[#0033FF] mt-1">•</span>
                  <span>Upload PDF, DOCX, or paste text</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0033FF] mt-1">•</span>
                  <span>Instant text extraction from any format</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0033FF] mt-1">•</span>
                  <span>Secure and confidential processing</span>
                </li>
              </ul>
              <p className="mt-6 text-sm font-medium text-[#A1A1AA]">Simple input. Zero complexity.</p>
            </div>

            {/* Phase 2 */}
            <div className="bg-white border border-black/5 p-8" data-testid="phase-analysis">
              <div className="w-16 h-16 bg-[#0033FF] flex items-center justify-center mb-6">
                <Brain size={32} className="text-white" weight="duotone" />
              </div>
              <div className="text-6xl font-black text-[#0033FF]/20 mb-4">2</div>
              <h4 className="text-2xl font-bold mb-4">AI Drives Analysis</h4>
              <ul className="space-y-3 text-[#52525B] font-light">
                <li className="flex items-start gap-2">
                  <span className="text-[#0033FF] mt-1">•</span>
                  <span>AI calculates job readiness score (0-100)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0033FF] mt-1">•</span>
                  <span>Identifies skill gaps and strengths</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0033FF] mt-1">•</span>
                  <span>Generates actionable recommendations</span>
                </li>
              </ul>
              <p className="mt-6 text-sm font-medium text-[#A1A1AA]">AI replaces manual screening.</p>
            </div>

            {/* Phase 3 */}
            <div className="bg-white border border-black/5 p-8" data-testid="phase-results">
              <div className="w-16 h-16 bg-[#0033FF] flex items-center justify-center mb-6">
                <ChartBar size={32} className="text-white" weight="duotone" />
              </div>
              <div className="text-6xl font-black text-[#0033FF]/20 mb-4">3</div>
              <h4 className="text-2xl font-bold mb-4">Get Interview-Ready</h4>
              <ul className="space-y-3 text-[#52525B] font-light">
                <li className="flex items-start gap-2">
                  <span className="text-[#0033FF] mt-1">•</span>
                  <span>Review detailed scorecards and insights</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0033FF] mt-1">•</span>
                  <span>Practice with AI interview simulator</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#0033FF] mt-1">•</span>
                  <span>Get real-time feedback and improvement tips</span>
                </li>
              </ul>
              <p className="mt-6 text-sm font-medium text-[#A1A1AA]">You review. You improve. You succeed.</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/auth')}
              className="bg-[#0033FF] text-white rounded-none font-bold px-8 py-4 hover:bg-[#002AE6] transition-colors inline-flex items-center gap-2"
            >
              Try TalentoX for FREE
              <ArrowRight size={20} weight="bold" />
            </button>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="px-6 md:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl sm:text-5xl tracking-tight font-black leading-tight mb-4">
              Is This Your Hiring Reality?
            </h3>
            <p className="text-lg font-light text-[#52525B] max-w-3xl mx-auto">
              Teams actively hiring face these costly gaps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Users, text: "High applicant volumes but no structured shortlisting" },
              { icon: TrendUp, text: "Hours spent manually screening resumes" },
              { icon: Lightning, text: "Slow shortlist-to-interview cycles" },
              { icon: Target, text: "Inconsistent candidate evaluation" },
              { icon: ChartBar, text: "No data-driven hiring decisions" },
              { icon: Gauge, text: "Scaling hiring without scalable intelligence" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-4 p-6 bg-[#F9F9FB] border border-black/5">
                <item.icon size={24} className="text-[#0033FF] flex-shrink-0 mt-1" weight="duotone" />
                <p className="font-light text-[#0A0A0A]">{item.text}</p>
              </div>
            ))}
          </div>

          <div className="bg-[#0033FF] text-white p-8 text-center">
            <p className="text-xl font-medium mb-4">
              The issue isn't sourcing candidates — it's screening and prioritization.
            </p>
            <p className="text-lg font-light opacity-90">
              That's exactly what TalentoX is designed to solve.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 md:py-32 bg-[#F9F9FB]">
        <div className="px-6 md:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h3 className="text-4xl sm:text-5xl tracking-tight font-black leading-tight mb-4">
              Everything You Need to Hire Smarter
            </h3>
            <p className="text-lg font-light text-[#52525B] max-w-3xl mx-auto">
              Build trust in every hiring decision with AI-powered intelligence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white border border-black/5 p-8 hover-lift" data-testid="feature-resume-analysis">
              <div className="w-12 h-12 bg-[#0033FF] flex items-center justify-center mb-6">
                <Brain size={24} className="text-white" weight="duotone" />
              </div>
              <h4 className="text-xl font-bold mb-3">AI Resume Analysis</h4>
              <p className="text-[#52525B] font-light leading-relaxed">
                Deep learning models extract skills, experience, and potential from any resume format. Get structured insights in seconds.
              </p>
            </div>

            <div className="bg-white border border-black/5 p-8 hover-lift" data-testid="feature-readiness-scoring">
              <div className="w-12 h-12 bg-[#0033FF] flex items-center justify-center mb-6">
                <Target size={24} className="text-white" weight="duotone" />
              </div>
              <h4 className="text-xl font-bold mb-3">Job Readiness Scoring</h4>
              <p className="text-[#52525B] font-light leading-relaxed">
                Objective 0-100 scoring system measures candidate preparedness across multiple dimensions with AI precision.
              </p>
            </div>

            <div className="bg-white border border-black/5 p-8 hover-lift" data-testid="feature-skill-gaps">
              <div className="w-12 h-12 bg-[#0033FF] flex items-center justify-center mb-6">
                <TrendUp size={24} className="text-white" weight="duotone" />
              </div>
              <h4 className="text-xl font-bold mb-3">Skill Gap Identification</h4>
              <p className="text-[#52525B] font-light leading-relaxed">
                Pinpoint specific areas for improvement with actionable learning recommendations and development paths.
              </p>
            </div>

            <div className="bg-white border border-black/5 p-8 hover-lift" data-testid="feature-interview-sim">
              <div className="w-12 h-12 bg-[#0033FF] flex items-center justify-center mb-6">
                <ChatsCircle size={24} className="text-white" weight="duotone" />
              </div>
              <h4 className="text-xl font-bold mb-3">AI Interview Simulation</h4>
              <p className="text-[#52525B] font-light leading-relaxed">
                Practice with our AI interviewer and receive real-time feedback on responses, communication, and readiness.
              </p>
            </div>

            <div className="bg-white border border-black/5 p-8 hover-lift">
              <div className="w-12 h-12 bg-[#0033FF] flex items-center justify-center mb-6">
                <ChartBar size={24} className="text-white" weight="duotone" />
              </div>
              <h4 className="text-xl font-bold mb-3">Interview-Ready Shortlists</h4>
              <p className="text-[#52525B] font-light leading-relaxed">
                Generate clear, data-backed candidate rankings helping teams move confidently from screening to interviews.
              </p>
            </div>

            <div className="bg-white border border-black/5 p-8 hover-lift">
              <div className="w-12 h-12 bg-[#0033FF] flex items-center justify-center mb-6">
                <Sparkle size={24} className="text-white" weight="duotone" />
              </div>
              <h4 className="text-xl font-bold mb-3">Structured Scorecards</h4>
              <p className="text-[#52525B] font-light leading-relaxed">
                AI-generated evaluation scorecards with strengths, gaps, and fit scores for consistent decision-making.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* For Employers */}
      <section className="py-24 md:py-32 bg-white">
        <div className="px-6 md:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-[#0033FF]/10 text-[#0033FF] text-xs font-bold uppercase tracking-wider mb-6">
                For Employers
              </span>
              <h3 className="text-4xl sm:text-5xl tracking-tight font-black leading-tight mb-6">
                Data-Driven Hiring Intelligence
              </h3>
              <p className="text-lg font-light text-[#52525B] mb-8">
                Access comprehensive candidate insights powered by AI. View readiness scores, skill assessments, 
                and interview performance in one unified dashboard.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle size={24} className="text-[#0033FF] flex-shrink-0 mt-1" weight="fill" />
                  <span className="font-light text-lg">Real-time candidate pipeline with AI-generated insights</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={24} className="text-[#0033FF] flex-shrink-0 mt-1" weight="fill" />
                  <span className="font-light text-lg">Automated skill matching and gap analysis</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={24} className="text-[#0033FF] flex-shrink-0 mt-1" weight="fill" />
                  <span className="font-light text-lg">Reduce time-to-hire by 75% with intelligent filtering</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle size={24} className="text-[#0033FF] flex-shrink-0 mt-1" weight="fill" />
                  <span className="font-light text-lg">Structured scorecards for consistent evaluation</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/auth')}
                className="bg-[#0033FF] text-white rounded-none font-bold px-8 py-4 hover:bg-[#002AE6] transition-colors inline-flex items-center gap-2"
                data-testid="employer-cta-button"
              >
                Start Hiring Smarter
                <ArrowRight size={20} weight="bold" />
              </button>
            </div>
            <div>
              <img
                src="https://static.prod-images.emergentagent.com/jobs/68f8c0f8-9e08-45d3-9309-aad3baf14c1b/images/6c0254d3dfa2d50dfd09e168abe2e7fc127c04f738216088072bddbbb3712cee.png"
                alt="Employer Dashboard"
                className="w-full h-auto border border-black/5"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#0033FF]">
        <div className="px-6 md:px-8 lg:px-12 max-w-4xl mx-auto text-center text-white">
          <h3 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black leading-none mb-6">
            Ready to Transform Your Career?
          </h3>
          <p className="text-xl font-light opacity-90 mb-8">
            Join thousands using AI to accelerate career growth and hiring success.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={() => navigate('/auth')}
              className="bg-white text-[#0033FF] rounded-none font-bold px-8 py-4 hover:bg-[#F9F9FB] transition-colors inline-flex items-center justify-center gap-2"
              data-testid="footer-cta-button"
            >
              Start Free Analysis
              <ArrowRight size={20} weight="bold" />
            </button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm font-light opacity-75">
            <div className="flex items-center gap-2">
              <CheckCircle size={18} weight="fill" />
              <span>No Credit Card</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} weight="fill" />
              <span>Instant Results</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} weight="fill" />
              <span>100% Free</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-white py-12">
        <div className="px-6 md:px-8 lg:px-12 max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
              <p className="text-2xl font-black tracking-tighter mb-2">TalentoX</p>
              <p className="text-sm font-light text-white/60">
                AI-Powered Career Intelligence Platform
              </p>
            </div>
            <p className="text-sm font-light text-white/60">
              © 2026 TalentoX. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
